import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { selectDaisyTheme } from '../../../../store/themes/themes.selectors';
import { selectFiltersSelected } from '../../../../store/base/filters/base-filters.selectors';
import { selectTrendFilters } from '../../../../store/base/filters/trend/trend.selectors';
import { selectUserToken } from '../../../../store/auth/authentication.selectors';

import { TREND_FILTER_TYPES } from '../../../../store/base/filters/trend/trend.types';

import { trendService } from '../../../../store/base/filters/trend/trend.services';
import { apiService } from '../../../../utils/engine/api.service';
import { WEB_PROCESS_FLOWS } from '../../../../utils/engine/pf-config.service';

import FiltersContainer from '../../../../components/base/filters/filters-container.component';
import TrendFiltersComponent from '../../../../components/base/filters/trend-filters/trend-filters.component';
import LineChartComponent from '../../../../components/charts/line-chart-component/line-chart.component';
import StackedChartComponent from '../../../../components/charts/stacked-chart-component/stacked-chart.component';
import ShareViewComponent, {
    onShareViewClick,
} from '../../../../components/base/share-view-component/share-view.component';
import GenerateReportComponent from '../../../../components/base/generate-report-component/generate-report.component';
import NoData from '../../../../components/no-data/no-data.component';
import PageLoadingComponent from '../../../../components/loading-component/page-loading-component';
import {
    createNotification,
    NOTIFICATION_TYPES,
} from '../../../../utils/notification.util';
import {
    base_grid,
    base_page,
    base_subnavigation_bar,
    base_widget_full,
    base_widget_half,
    muted_border,
} from '../../../../utils/styles/tailwind-styles';

const SalesPage = () => {
    const [salesValue, setSalesValue] = useState(null);
    const [salesVolume, setSalesVolume] = useState(null);
    const [averageData, setAverageData] = useState(null);
    const [salesValuePlottable, setSalesValuePlottable] = useState(null);
    const [salesVolumePlottable, setSalesVolumePlottable] = useState(null);
    const [averageDataPlottable, setAverageDataPlottable] = useState(null);

    const [isLoading, setIsLoading] = useState(false);
    const [viewType, setViewType] = useState(TREND_FILTER_TYPES.view.default);
    const [trendFiltersVisible, setTrendFiltersVisible] = useState(false);

    const daisyTheme = useSelector(selectDaisyTheme);
    const selectedFilters = useSelector(selectFiltersSelected);
    const trendFilters = useSelector(selectTrendFilters);

    const userToken = useSelector(selectUserToken);

    const handleViewTypeChange = (event) => {
        setViewType(event);
    };

    const onShare = async (recipientsSelected, additionalMessage) => {
        setIsLoading(true);
        try {
            await onShareViewClick(
                userToken,
                selectedFilters,
                trendFilters,
                recipientsSelected,
                additionalMessage
            );
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
        }
    };

    const fetchSalesTrendHelper = async () => {
        setIsLoading(true);
        try {
            const trendData = await trendService.loadSalesTrendData(
                selectedFilters,
                trendFilters
            );

            if (
                trendData['freqTrendAvg']['channels'][0]['dataKey'] !== 'Value'
            ) {
                setSalesValue(trendData['freqTrendCost']);
                setSalesVolume(trendData['freqTrendQty']);
                setAverageData(trendData['freqTrendAvg']);

                setSalesValuePlottable(
                    trendService.convertTrendDataToPlottable(
                        trendData['freqTrendCost'],
                        daisyTheme
                    )
                );
                setSalesVolumePlottable(
                    trendService.convertTrendDataToPlottable(
                        trendData['freqTrendQty'],
                        daisyTheme
                    )
                );
                setAverageDataPlottable(
                    trendService.convertTrendDataToPlottable(
                        trendData['freqTrendAvg'],
                        daisyTheme
                    )
                );
            }

            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
        }
    };

    const onGenerateReportClick = async (reportParams) => {
        setIsLoading(true);
        try {
            const trendData = await trendService.loadSalesTrendData(
                selectedFilters,
                trendFilters,
                reportParams
            );
            if (trendData.message && trendData.message === 'Processing Request')
                createNotification(
                    'Report request sent',
                    NOTIFICATION_TYPES.SUCCESS
                );

            if (reportParams['cronText'] != null) {
                await apiService.scheduleTrendProcess(
                    WEB_PROCESS_FLOWS.SALES,
                    selectedFilters,
                    trendFilters,
                    reportParams,
                    reportParams['cronText']
                );
            }
            setIsLoading(false);
        } catch (err) {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSalesTrendHelper();
    }, [selectedFilters, trendFilters]);

    useEffect(() => {
        if (salesValue !== null) {
            setSalesValuePlottable(
                trendService.convertTrendDataToPlottable(salesValue, daisyTheme)
            );
        }
        if (salesVolume !== null) {
            setSalesVolumePlottable(
                trendService.convertTrendDataToPlottable(
                    salesVolume,
                    daisyTheme
                )
            );
        }
        if (averageData !== null) {
            setAverageDataPlottable(
                trendService.convertTrendDataToPlottable(
                    averageData,
                    daisyTheme
                )
            );
        }
    }, [daisyTheme]);

    return (
        <>
            <div className={base_subnavigation_bar}>
                <div className="flex w-full h-full justify-between items-center max-w-8xl">
                    <span className={'flex items-center gap-1'}>
                        <a
                            className="flex h-full items-center btn btn-ghost font-bold btn-sm md:btn-md lg:btn-lg text-base normal-case text-base-content text-opacity-80 tracking-wider"
                            href={'/base/sales'}
                        >
                            Sales
                        </a>

                        <ShareViewComponent onClick={onShare} />
                        <GenerateReportComponent
                            frequency={true}
                            dateRange={true}
                            includeSoh={true}
                            onGenerate={onGenerateReportClick}
                        />
                    </span>

                    <span className="flex items-center justify-between">
                        <TrendFiltersComponent
                            className="z-10"
                            onViewChange={handleViewTypeChange}
                            viewState={viewType}
                            visible={trendFiltersVisible}
                            toggleOpen={() => {
                                setTrendFiltersVisible(!trendFiltersVisible);
                            }}
                        />
                    </span>
                </div>
            </div>

            <div className={`${base_page}`}>
                <div className={`${base_grid}`}>
                    <FiltersContainer className={'col-span-full'} />

                    {isLoading ? (
                        <PageLoadingComponent />
                    ) : salesValuePlottable === null &&
                      salesVolumePlottable === null &&
                      averageDataPlottable === null ? (
                        <NoData className="col-span-full" />
                    ) : (
                        <>
                            <div
                                className={`${base_widget_half} ${muted_border}`}
                            >
                                {viewType.value === 'lines' ? (
                                    <LineChartComponent
                                        data={salesValuePlottable}
                                        title="Sales Trend"
                                        type={'Value'}
                                        interactionMode="nearest"
                                        onClickHideOthers={false}
                                    />
                                ) : (
                                    <StackedChartComponent
                                        data={salesValuePlottable}
                                        title="Sales Trend"
                                        type={'Value'}
                                        interactionMode="nearest"
                                        onClickHideOthers={false}
                                    />
                                )}
                            </div>

                            <div
                                className={`${base_widget_half} ${muted_border}`}
                            >
                                {viewType.value === 'lines' ? (
                                    <LineChartComponent
                                        data={salesVolumePlottable}
                                        title="Sales Trend"
                                        type={'Volume'}
                                        interactionMode="nearest"
                                        onClickHideOthers={false}
                                    />
                                ) : (
                                    <StackedChartComponent
                                        data={salesVolumePlottable}
                                        title="Sales Trend"
                                        type={'Volume'}
                                        interactionMode="nearest"
                                        onClickHideOthers={false}
                                    />
                                )}
                            </div>

                            <div
                                className={`${base_widget_full} ${muted_border}`}
                            >
                                {viewType.value === 'lines' ? (
                                    <LineChartComponent
                                        data={averageDataPlottable}
                                        title="Average"
                                        type={'Price'}
                                        interactionMode="nearest"
                                        onClickHideOthers={false}
                                    />
                                ) : (
                                    <StackedChartComponent
                                        data={averageDataPlottable}
                                        title="Average"
                                        type={'Price'}
                                        onClickHideOthers={false}
                                    />
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default SalesPage;
